import { EventType, useI18N } from "@sundae/react-hooks";
import type { IComposedTx } from "@sundaeswap/core";
import {
  EScriptType,
  IBaseArgs,
  IClaimArgs,
  IDepositArgs,
  IUpdateArgs,
  IWithdrawArgs,
  TasteTestLucid,
} from "@sundaeswap/taste-test";
import { useMutation } from "@tanstack/react-query";
import addMinutes from "date-fns/addMinutes";
import { UTxO, type Credential, type Tx, type TxComplete } from "lucid-cardano";
import { useCallback, useMemo } from "react";

import { useWalletDetails } from "@sundae/wallet-react";
import { ADA_METADATA } from "../../../constants/cardano.constants";
import { useServerTime } from "../../../gql/hooks/time.query";
import { useReservedUtxos } from "../../../hooks/taste-test/useReservedUtxos";
import { useTasteTestWithdrawAmounts } from "../../../hooks/taste-test/useTasteTestWithdrawAmounts";
import { useEventTracking } from "../../../hooks/useEventTracking";
import {
  ITransactionErrorShape,
  useTransactionErrorHandler,
} from "../../../hooks/useTransactionErrorHandler";
import { useAppContext } from "../../../stores";
import {
  EOrderFlowState,
  EOrderTransactionBuilderState,
  OrderActions,
  useOrderContext,
} from "../../../stores/OrderContext";
import { slotToUnix } from "../../../utils/slots.utils";
import { TransactionErrorHandler } from "../../../utils/transactions/TransactionErrorHandler.class";
import { TActionType, useTasteTestContext } from "../context";
import { getLpTokenFromTasteTest } from "../utils/getLpTokenFromReservedUtxo";

let shouldUseBlockfrost = false;

export const useTasteTestTx = ({ type }: { type: TActionType }) => {
  const { t } = useI18N("transactions");
  const {
    derived: { depositAssetAmount },
    handlers: { setActionType, setReservedUtxos, updatePersistedContributionsHistory },
    state: { persistedContributionsHistory, reservedUtxos, tasteTest },
  } = useTasteTestContext();
  const { total: totalWithdrawalAmount } = useTasteTestWithdrawAmounts();
  const handleEventTracking = useEventTracking();
  const { handleTransactionError } = useTransactionErrorHandler();
  const { data: serverTime } = useServerTime();
  const {
    details: { mainAddress },
  } = useWalletDetails();
  const {
    state: { sdk },
    // dispatch: appDispatch,
  } = useAppContext();
  const {
    dispatch,
    state: {
      derived: { transaction },
    },
  } = useOrderContext();
  const { getReservedUtxos } = useReservedUtxos({
    owner: mainAddress,
    tasteTestPolicyId: tasteTest?.policyId,
    type,
    handleError: () => {
      console.log("Could not retrieve a reserved UTxO. Opting for Blockfrost instead.");
    },
  });

  const getOrderAmount = useCallback(() => {
    if (type === "withdraw") {
      return totalWithdrawalAmount?.amount?.toString() ?? "0";
    }

    if (type === "claim") {
      const lpAssetAmount = getLpTokenFromTasteTest(tasteTest, reservedUtxos);
      return lpAssetAmount?.amount?.toString() ?? "0";
    }

    return depositAssetAmount?.amount?.toString() ?? "0";
  }, [depositAssetAmount, totalWithdrawalAmount?.amount, type]);
  const serverNow = useMemo(
    () => (serverTime?.now ? new Date(serverTime.now).getTime() : Date.now()),
    [serverTime]
  );

  const getConfigByType = useCallback(
    (utxos?: UTxO[]) => {
      if (!sdk) {
        throw new Error("Could not find SDK instance.");
      }

      if (!tasteTest) {
        throw new Error("Attempted to interact with a TasteTest before it was loaded.");
      }

      let config: IDepositArgs | IWithdrawArgs | IUpdateArgs | IClaimArgs;

      const credential: Credential = {
        type: "Script",
        hash: tasteTest.scripts.tasteTestValidator.scriptHash,
      };

      const scripts: IBaseArgs["scripts"] = {
        policy: {
          type: EScriptType.OUTREF,
          value: {
            hash: tasteTest.scripts.tasteTestPolicy.scriptHash,
            outRef: {
              txHash: String(tasteTest.scripts.tasteTestPolicy.utxo?.txHash),
              outputIndex: Number(tasteTest.scripts.tasteTestPolicy.utxo?.index),
            },
          },
        },
        validator: {
          type: EScriptType.OUTREF,
          value: {
            hash: tasteTest.scripts.tasteTestValidator.scriptHash,
            outRef: {
              txHash: String(tasteTest.scripts.tasteTestValidator.utxo?.txHash),
              outputIndex: Number(tasteTest.scripts.tasteTestValidator.utxo?.index),
            },
          },
        },
      };

      const lucid = sdk.builder().lucid;
      const validatorAddress = lucid.utils.credentialToAddress(credential);
      if (!validatorAddress) {
        throw new Error("Could not derive validator address from TT validator hash!");
      }

      if (type === "deposit" && depositAssetAmount) {
        config = {
          utxos,
          time: serverNow,
          deadline: slotToUnix(tasteTest.endDate.slot) * 1000,
          validatorAddress,
          assetAmount: depositAssetAmount,
          scripts,
          tasteTestType: tasteTest.type,
          updateFallback: true,
        } as IDepositArgs;
      } else if (type === "update" && depositAssetAmount) {
        config = {
          utxos,
          time: serverNow,
          deadline: slotToUnix(tasteTest.endDate.slot) * 1000,
          validatorAddress,
          assetAmount: depositAssetAmount,
          scripts,
          tasteTestType: tasteTest.type,
        } as IUpdateArgs;
      } else if (type === "claim") {
        config = {
          utxos,
          time: serverNow,
          deadline: slotToUnix(tasteTest.endDate.slot) * 1000,
          rewardFoldPolicyId: tasteTest.scripts.rewardFoldPolicy.scriptHash,
          scripts,
          validatorAddress,
          burnFoldToken: false,
          tasteTestType: tasteTest.type,
        } as IClaimArgs;
      } else {
        config = {
          utxos,
          validatorAddress,
          time: serverNow,
          deadline: slotToUnix(tasteTest.endDate.slot) * 1000,
          penaltyAddress: tasteTest.parameters.penaltyAddress,
          scripts,
          tasteTestType: tasteTest.type,
        } as IWithdrawArgs;
      }

      return config;
    },
    [serverTime, tasteTest, depositAssetAmount, type]
  );

  const handleCancelTx = useCallback(() => {
    OrderActions.setOrderConsent({ checked: false }, dispatch);
    OrderActions.setOrderFlowState(
      EOrderFlowState.initial,
      dispatch,
      "useTasteTest.handleCancelTx"
    );
    setActionType(type);

    // Resetting the transaction builder state as we don't want to show previously calculated fees on the next swap screen.
    OrderActions.setTransaction({}, dispatch, "useTasteTestTx.handleCancelTx");
  }, [dispatch]);

  const { mutateAsync: buildTx } = useMutation(
    [type, sdk, tasteTest],
    async () => {
      return TransactionErrorHandler.buildTransaction(async () => {
        if (!sdk) {
          throw new Error("Could not find SDK instance.");
        }

        if (!tasteTest) {
          throw new Error("Attempted to interact with a TasteTest before it was loaded.");
        }

        const lucid = sdk.builder().lucid;
        let utxos: UTxO[] | undefined;
        if (!shouldUseBlockfrost) {
          utxos = await getReservedUtxos();
          setReservedUtxos(utxos);
        }

        const TT = new TasteTestLucid(lucid);
        const config = getConfigByType(utxos);

        switch (type) {
          case "update":
            return TT.update(config as IUpdateArgs);
          case "withdraw":
            return TT.withdraw(config as IWithdrawArgs);
          case "claim":
            return TT.claim(config as IClaimArgs);
          default:
          case "deposit":
            return TT.deposit(config as IDepositArgs);
        }
      });
    },
    {
      cacheTime: 0,
      onMutate() {
        setActionType(type);
        OrderActions.setTransactionState(
          EOrderTransactionBuilderState.BUILD,
          dispatch,
          "useTasteTestTx.buildTx.onMutate"
        );
      },
      async onError({ error, transactionState }: ITransactionErrorShape) {
        OrderActions.setTransactionState(
          EOrderTransactionBuilderState.ERROR,
          dispatch,
          "useTasteTestTx.buildTx.onError"
        );

        handleTransactionError(
          error,
          {
            resolutionButtonText: t("transaction.error_reset"),
            resolutionAction: () => {
              shouldUseBlockfrost = true;
              OrderActions.setOrderFlowState(EOrderFlowState.previewCancelled, dispatch);
            },
          },
          transactionState,
          getConfigByType()
        );
      },
      async onSuccess(data: IComposedTx<Tx, TxComplete>) {
        if (!data) {
          return;
        }

        shouldUseBlockfrost = false;
        const completeTx = await data.build();

        OrderActions.setTransaction(
          { builtTx: data, completeTx, state: EOrderTransactionBuilderState.SUCCESS },
          dispatch,
          "useTasteTestTx.buildTx.onSuccess"
        );
      },
    }
  );

  const { mutateAsync: submitTx } = useMutation(
    [type, transaction?.builtTx],
    async () => {
      return TransactionErrorHandler.handleSDKMutation(
        // we are not passing a buildFn here because we already have a built tx available
        undefined,
        transaction?.builtTx || (await buildTx())
      );
    },
    {
      cacheTime: 0,
      async onError({ error, transactionState, cbor }: ITransactionErrorShape) {
        handleTransactionError(
          error,
          {
            resolutionButtonText: t("transaction.error_reset"),
            resolutionAction: handleCancelTx,
          },
          transactionState,
          await getConfigByType(),
          cbor
        );
      },
      onMutate() {
        handleEventTracking({
          eventType: EventType.Event,
          source: "TasteTest",
          value: `taste_test_execution_${type}`,
        });
        OrderActions.setOrderFlowState(
          EOrderFlowState.submitting,
          dispatch,
          "useTasteTestTx.builtTx.onMutate"
        );
      },
      onSuccess(txHash) {
        const now = new Date();

        if (tasteTest) {
          updatePersistedContributionsHistory?.([
            ...(persistedContributionsHistory ?? []),
            {
              createdAt: now.getTime(),
              details: {
                assetId: depositAssetAmount?.metadata?.assetId ?? ADA_METADATA.assetId,
                amount: getOrderAmount(),
              },
              expiresAt: addMinutes(now, 5).getTime(),
              id: tasteTest.policyId,
              txHash,
              type,
            },
          ]);
        }

        return txHash;
      },
    }
  );

  return {
    buildTx,
    submitTx,
    handleCancelTx,
  };
};
